import { useSyncExternalStore } from 'react';
import { docViewerKind, type DocViewerKind } from './doc-viewer';
import type { WorkspaceFile } from './workspace-files';
import { effectiveContent } from './file-record';
import type { WriteDirectory } from './file-browser';

/**
 * The document viewer's open state lives HERE — in one module-level presenter — and NOT in whichever
 * component the user clicked. This is the web port of native's `DocumentViewerPresenter`
 * (`ios/.../Features/Misc/DocumentViewer.swift`, `android/.../features/DocumentViewer.kt`), which is a
 * singleton state holder with the viewer hosted once at the app root (`RootView.swift`'s overlay chain /
 * `AppRoot.kt`'s ZStack). Same shape, same reason.
 *
 * The reason is a real defect, not symmetry for its own sake. The open state used to be a `useState`
 * inside `SentAttachmentList`, which renders inside a chat message row — so the viewer's lifetime was the
 * ROW's lifetime. An optimistic just-sent message re-keys when it reconciles to its server id, and that
 * unmounted an open report mid-read. A presenter plus one root-level host decouples the viewer from every
 * row, panel and screen that can open it: whoever opened it may unmount freely.
 *
 * Read it in React with `useSyncExternalStore` rather than a context provider — the same module-level-store
 * precedent as `lib/modal-escape.ts` and `lib/unread-notifications-store.ts`. A provider would have to be
 * threaded through `MessageRow`'s four `SentAttachmentList` call sites, and through the non-framed pages,
 * to say something no component owns anyway.
 */
export interface DocViewerRequest {
  file: WorkspaceFile;
  /** Which viewer to render, resolved BY THE CALLER — see `DocViewerModal`'s `kind` prop. A caller has the
   *  attachment's `mimeType`; a `WorkspaceFile` does not, so re-deriving downstream loses the MIME rescue.
   *
   *  `null` is a first-class value: the file opens in the SAME modal, which says it cannot be previewed and
   *  offers Download / Open in new tab / Discuss. Every file type therefore has one presentation — before
   *  this, a `null` kind fell out of the viewer entirely and each caller improvised (navigate away, or
   *  `window.open` a tab), so "what happens when I click a file" depended on which list you clicked from. */
  kind: DocViewerKind | null;
  /** The BI page_name the viewer's own actions report under. */
  pageName: string;
  /** This file's own page as a new-tab URL (`fileNewTabLink`), when the OPENER can name one.
   *
   *  Only the caller knows: a Files row has the workspace slug, a chat attachment has none — a chat file is
   *  identified by (conversation, message, name), and no route of ours carries that. So a chat attachment
   *  leaves this absent and `buildFileActions` tiers "Open in new tab" down to the file's own signed URL,
   *  which is also exactly what the chip beside it already does. Inventing a `?url=<signed>` route instead
   *  would put the signed URL in the address bar and history — the very thing this replaced — and cost a new
   *  `PAGE_NAMES` entry plus two native mirrors for a screen neither phone has. */
  fileHref?: string;
  /** The workspace the file belongs to, for the viewer header's subtitle. Only the surfaces that know it
   *  (the workspace gallery) pass it; absent it, the subtitle omits the segment rather than fabricating it. */
  workspaceName?: string;
  /** Open straight into the editor rather than the reader.
   *
   *  This is how the pencil works from a ROW: the editor lives inside the viewer, so a row's pencil opens
   *  the viewer and says which mode to land in. Carrying it on the request — rather than opening the viewer
   *  and poking it a moment later — means there is no window in which the wrong mode is on screen, and no
   *  timing to get wrong when the file is slow. Ignored for a file the editor refuses (see `DocEditView`),
   *  which shows its own "not editable" state in the same card. */
  editing?: boolean;
  /** WHICH file-system directory this file lives in — `workspaces` (default) or `brain`, an agent's own
   *  folder. The editor's save rides it, so a file opened from an agent's page is written back there. */
  directory?: WriteDirectory;
  /**
   * Whether this file has a place in the account's FILE TREE — i.e. it is a stored workspace file rather
   * than a chat attachment or a task artifact.
   *
   * It is the gate on the viewer's three FILING actions — **Move to… / Send a copy / Share (grant access)**
   * — each of which needs the file to exist somewhere addressable: a folder to move between, a stored body to
   * hand a copy of, and a `FileSystem.File.id` (or the workspace path the server mints one from) to grant
   * against. A chat attachment has none of the three, so all three rows are absent there rather than opening
   * a dialog that can only fail. Same gate and same reasoning as native's
   * `DocumentViewerRequest.artifact`, which is `nil` for exactly these openers.
   *
   * It is a field of its own rather than `fileHref != null`, even though the two coincide today. `fileHref`
   * answers "can we name a PAGE for this file" — a routing question, allowed to change for routing reasons;
   * this answers "does this file live in the tree". Deriving one from the other would mean a future new-tab
   * change silently adding or removing three actions.
   *
   * Defaults to `false`: a caller that does not know cannot offer them.
   */
  inFileTree?: boolean;
  /**
   * Set when this is a NEW file rather than one that exists: the editor opens holding a draft — a name, a
   * description and a destination alongside the body — and Save creates it.
   *
   * The `+` used to open a small dialog that collected those, wrote an EMPTY file, and then opened the
   * editor on it. Two modals for one act, the second landing on a blank document the first had just made.
   * One surface now, and it is the surface the file will be read and edited in ever after.
   *
   * `folder` is where the browser was looking, as the destination's starting value — the common case is
   * "here", and the picker is one click away for the rest.
   */
  draft?: { folder: string };
  /**
   * The file's stable workspace address (`MessageAttachment.vfsPath` or a workspace-path link), when the
   * opener has one. It is what LIVE REFRESH re-reads: while the viewer is open, every file-system change
   * re-resolves this path and swaps the new body in (`lib/use-live-doc-file.ts`). Absent for a plain-URL
   * attachment, which is immutable and has nothing to re-read.
   */
  vfsPath?: string;
  /**
   * Opened FROM THE CHAT — dock it beside the conversation rather than above it. Honoured only while the
   * chat can host a pane (open, wide); otherwise the same request falls back to the modal. See
   * `lib/chat-file-pane.ts` and `docs/superpowers/specs/2026-10-09-chat-file-pane-design.md`.
   */
  besideChat?: boolean;
  /**
   * Set by Discuss alongside `besideChat`: the file is being handed to a chat that is about to open for it.
   * Until that chat opens the file is drawn nowhere rather than as the modal, which would only flash on its
   * way to the pane. A `besideChat` request WITHOUT it (a chat link that resolved after the chat was closed)
   * has no chat coming, so it opens as the modal. See `useBesideChatWaiting`.
   */
  handoff?: boolean;
}

/** What the host reads. `id` distinguishes two successive opens of the same file so the host can re-key
 *  (and therefore reset the modal's query/scroll state) instead of silently reusing the old instance. */
export interface OpenDocViewer extends DocViewerRequest {
  id: number;
}

let current: OpenDocViewer | null = null;
let nextId = 1;
const listeners = new Set<() => void>();

const emit = (): void => {
  for (const listener of [...listeners]) listener();
};

/**
 * What the Files `+` does: open the editor on a new, unsaved file.
 *
 * `kind` is markdown because that is the default extension a bare name gets (`DEFAULT_DRAFT_EXTENSION`),
 * and it only decides whether the Preview tab is offered — the name the user types is what actually
 * settles the file's type on save.
 */
export function openFileDraft(start: { folder: string; pageName: string; directory?: WriteDirectory }): void {
  openDocViewer({
    file: { name: '', content: '' } as WorkspaceFile,
    kind: 'markdown',
    pageName: start.pageName,
    editing: true,
    directory: start.directory,
    draft: { folder: start.folder },
  });
}

/** Presents the document viewer at the root. Replaces whatever was open. */
export function openDocViewer(request: DocViewerRequest): void {
  current = { ...request, id: nextId++ };
  emit();
}

/** What "clicking a file row" does: classify it and open the viewer — for EVERY kind, including `null`.
 *
 *  It used to open only the viewable kinds and hand `null` back so each caller could improvise, which is how
 *  one product ended up with three answers to "what happens when I click a file": the gallery navigated to
 *  another page, the cross-workspace list called `window.open`, and a card did something else again. The
 *  modal is now the single presentation and a `null` kind is a state INSIDE it.
 *
 *  Still returns the kind, because callers legitimately care (a row uses it to decide whether a plain click
 *  should `preventDefault` its own href). */
export function openFileForRow(
  item: WorkspaceFile,
  pageName: string,
  fileHref?: string,
  workspaceName?: string,
  options?: { editing?: boolean; directory?: WriteDirectory; besideChat?: boolean; handoff?: boolean },
): DocViewerKind | null {
  const kind = docViewerKind(item.name, effectiveContent(item));
  // A ROW is by definition a row over the file system, so the viewer it opens gets the filing actions — see
  // `inFileTree`. The chat/task openers call `openDocViewer` directly and leave it false. `editing` rides
  // along for a row's pencil, which opens the viewer straight into its editor.
  openDocViewer({ file: item, kind, pageName, fileHref, workspaceName, inFileTree: true, editing: options?.editing, directory: options?.directory, besideChat: options?.besideChat, handoff: options?.handoff });
  return kind;
}

/**
 * Update the OPEN request in place, without re-keying it. The viewer can be drawn as the chat's pane or as the
 * modal, and moving between the two (a resize, the `/chats` stacked layout) remounts it; whatever the viewer
 * learned while open has to live HERE to survive that. Today that is the live-refreshed `file`
 * (`lib/use-live-doc-file.ts`) and whether the editor is open (`lib/chat-file-pane.ts` freezes the
 * presentation while it is). Discuss also moves an open file beside the chat it opens (`besideChat`).
 * A no-op when `id` is no longer the open request.
 */
export function updateOpenDocViewer(id: number, patch: Partial<Pick<DocViewerRequest, 'file' | 'editing' | 'besideChat' | 'handoff'>>): void {
  if (current?.id !== id) return;
  current = { ...current, ...patch };
  emit();
}

/** Dismisses the viewer. A no-op (and no notification) when nothing is open. */
export function dismissDocViewer(): void {
  if (current == null) return;
  current = null;
  emit();
}

/** The open request, or `null`. Stable identity between changes, as `useSyncExternalStore` requires. */
export const currentDocViewer = (): OpenDocViewer | null => current;

/** SSR/hydration snapshot: nothing is ever open on the server. */
const serverDocViewer = (): OpenDocViewer | null => null;

export function subscribeDocViewer(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** React binding for the presenter — used by the single root-level host. */
export function useDocViewer(): OpenDocViewer | null {
  return useSyncExternalStore(subscribeDocViewer, currentDocViewer, serverDocViewer);
}

/** Test seam: how many hosts/readers are subscribed. */
export const docViewerListenerCount = (): number => listeners.size;

/** Test seam: drop any open request without notifying, so one test can't leak into the next. */
export function resetDocViewerForTest(): void {
  current = null;
  listeners.clear();
}
